import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

// ── TWP earth-tone palette so popups match JobApprovalModal aesthetic ─────
const VARIANTS = {
  success: { gradient: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', icon: '✅', label: 'SUCCESS' },
  warning: { gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', icon: '⚠️', label: 'HEADS UP' },
  error:   { gradient: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)', icon: '⚠️', label: 'ERROR' },
  info:    { gradient: 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)', icon: 'ℹ️', label: 'NOTICE' },
  confirm: { gradient: 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)', icon: '❓', label: 'CONFIRM' },
};

// ── Internal modal renderer — rendered once at root by NotificationProvider
function ModalDisplay({ state, onClose }) {
  if (!state) return null;
  const v          = VARIANTS[state.variant] || VARIANTS.info;
  const isConfirm  = state.variant === 'confirm';

  return (
    <div
      onClick={isConfirm ? undefined : onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(248, 244, 240, 0.55)',
        backdropFilter: 'blur(22px) saturate(140%)',
        WebkitBackdropFilter: 'blur(22px) saturate(140%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
        animation: 'twpNotifFadeIn 0.25s ease-out',
        fontFamily: "'Segoe UI', system-ui, sans-serif",
      }}
    >
      <style>{`
        @keyframes twpNotifFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes twpNotifSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0)   scale(1);    }
        }
      `}</style>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '500px',
          background: '#ffffff', borderRadius: '24px',
          boxShadow: '0 30px 80px rgba(60, 30, 10, 0.25), 0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid rgba(139, 69, 19, 0.08)',
          animation: 'twpNotifSlideUp 0.35s cubic-bezier(0.2, 0.9, 0.3, 1.1)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{ background: v.gradient, padding: '24px 32px', color: '#ffffff', position: 'relative' }}>
          {!isConfirm && (
            <button
              onClick={onClose}
              style={{
                position: 'absolute', top: '16px', right: '16px',
                background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%',
                width: '32px', height: '32px', color: '#ffffff', fontSize: '18px',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >×</button>
          )}
          <div style={{ fontSize: '12px', opacity: 0.9, fontWeight: 700, marginBottom: '6px', letterSpacing: '1px' }}>
            {v.icon} {v.label}
          </div>
          {state.title && (
            <div style={{ fontSize: '20px', fontWeight: 800, letterSpacing: '-0.3px', lineHeight: 1.25 }}>
              {state.title}
            </div>
          )}
        </div>

        {/* Body */}
        {(state.message || state.details) && (
          <div style={{ padding: '24px 32px' }}>
            {state.message && (
              <div style={{
                fontSize: '14px', color: '#1a1a1a', lineHeight: 1.6,
                marginBottom: state.details ? '12px' : '0',
                whiteSpace: 'pre-line',
              }}>
                {state.message}
              </div>
            )}
            {state.details && (
              <div style={{
                padding: '12px 14px', background: '#faf6f3',
                border: '1px solid #f0e6dc', borderRadius: '10px',
                fontSize: '12px', color: '#666',
                whiteSpace: 'pre-line',
                fontFamily: '"SF Mono", Monaco, Consolas, monospace',
                wordBreak: 'break-word',
              }}>
                {state.details}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '16px 32px 24px',
          borderTop: '1px solid #f0e6dc',
          background: '#faf6f3',
          display: 'flex', gap: '10px',
        }}>
          {state.secondaryLabel && (
            <button
              onClick={state.onSecondary || onClose}
              style={{
                flex: 1, padding: '14px',
                background: '#ffffff', color: '#666',
                border: '2px solid #e5e5e5', borderRadius: '12px',
                fontSize: '14px', fontWeight: 700, cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >{state.secondaryLabel}</button>
          )}
          <button
            onClick={state.onPrimary || onClose}
            style={{
              flex: state.secondaryLabel ? 2 : 1, padding: '14px',
              background: v.gradient, color: '#ffffff',
              border: 'none', borderRadius: '12px',
              fontSize: '14px', fontWeight: 800, cursor: 'pointer',
              textTransform: 'uppercase', letterSpacing: '0.5px',
              boxShadow: '0 4px 12px rgba(60, 30, 10, 0.2)',
              fontFamily: 'inherit',
            }}
          >{state.primaryLabel || 'OK'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Context ──────────────────────────────────────────────────────────────
const NotificationContext = createContext(null);

/**
 * useNotification() — call this from any component to fire popups.
 *
 *   const notify = useNotification();
 *   notify.success('Saved!', 'Customer details updated.');
 *   notify.error('Could not load jobs', 'Network problem — try again.', e?.message);
 *   notify.warning('Heads up', 'This estimate is older than 30 days.');
 *   notify.info('Notice', 'PIN will expire in 1 hour.');
 *   notify.confirm('Delete this contract?', 'Cannot be undone.', {
 *     onConfirm: () => deleteContract(id),
 *     confirmLabel: 'Delete',
 *   });
 *
 * Must be used inside <NotificationProvider>.
 */
export function useNotification() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotification must be used inside <NotificationProvider>');
  return ctx;
}

// ── Provider ─────────────────────────────────────────────────────────────
export function NotificationProvider({ children }) {
  const [state, setState] = useState(null);
  const hide = useCallback(() => setState(null), []);

  const api = useMemo(() => {
    const show = (opts) => setState({ ...opts });
    return {
      success: (title, message, details) => show({ variant: 'success', title, message, details }),
      error:   (title, message, details) => show({ variant: 'error',   title, message, details }),
      warning: (title, message, details) => show({ variant: 'warning', title, message, details }),
      info:    (title, message, details) => show({ variant: 'info',    title, message, details }),
      confirm: (title, message, options = {}) => show({
        variant:        'confirm',
        title,
        message,
        primaryLabel:   options.confirmLabel || 'Confirm',
        secondaryLabel: options.cancelLabel  || 'Cancel',
        onPrimary:      () => { setState(null); options.onConfirm && options.onConfirm(); },
        onSecondary:    () => { setState(null); options.onCancel  && options.onCancel();  },
      }),
      hide,
    };
  }, [hide]);

  return (
    <NotificationContext.Provider value={api}>
      {children}
      <ModalDisplay state={state} onClose={hide} />
    </NotificationContext.Provider>
  );
}

export default NotificationProvider;
